// Typ: FindAreaEquilateralTask
// Temat: Trójkąt równoboczny → Pole
// Wzór:  P = (a^2√3) / 4

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// ===== generator =====
export function generateTask() {
  const a = Math.floor(Math.random() * 8) + 3; // bok 3..10
  return {
    type: "FindAreaEquilateralTask",
    data: { a }
  };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest trójkąt równoboczny o boku
      <InlineMath math={`\\ a=${a}`} />. Oblicz jego pole{" "}
      <span style={{ whiteSpace: "nowrap" }}>
        <InlineMath math={'P'} />.
      </span>
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'P'}
        placeholder="np. (25√3)/4, (9√3)/4, 10.83"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a } = data;
  const expected = (a * a * Math.sqrt(3)) / 4;

  // 1) standardowy parse z RootFractionInput
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba dziesiętna (obsługa przecinka)
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptujemy również zapis dziesiętny z dokładnością do 0.01
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a } = data;
  const a2 = a * a;

  // P = (a^2 √3) / 4  -> skróć a^2/4
  const g = gcd(a2, 4);
  const n = a2 / g;
  const d = 4 / g;

  const latex = d === 1
    ? (n === 1 ? 'P = \\sqrt{3}' : `P = ${n} \\sqrt{3}`)
    : `P = \\dfrac{${n} \\sqrt{3}}{${d}}`;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={latex} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a } = data;
  const a2 = a * a;

  const g = gcd(a2, 4);
  const n = a2 / g;
  const d = 4 / g;

  const simplified =
    d === 1
      ? (n === 1 ? 'P = \\sqrt{3}' : `P = ${n} \\sqrt{3}`)
      : `P = \\dfrac{${n} \\sqrt{3}}{${d}}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór na pole trójkąta równobocznego:</div>
      <BlockMath math={'P = \\dfrac{a^{2}\\sqrt{3}}{4}'} />

      <div><strong>Krok 2.</strong> Podstawiamy <InlineMath math={`a=${a}`} />:</div>
      <BlockMath math={`P = \\dfrac{${a}^{2}\\sqrt{3}}{4} = \\dfrac{${a2}\\sqrt{3}}{4}`} />

      {g > 1 && (
        <>
          <div><strong>Krok 3.</strong> Upraszczamy ułamek:</div>
          <BlockMath math={simplified} />
        </>
      )}
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "P")} />
    </div>
  );
}
